/**
 * The game log dock. UI_UX section 7.
 *
 * Lines come from the engine's own `renderEvent`, so the log and the rules
 * speak with one voice. Turn changes become dividers, and any card name a line
 * mentions is clickable: clicking zooms that card, which is the fastest way to
 * answer "what just killed me".
 */

import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import type { GameState } from '@/engine';
import { profile } from '@/engine';
import { UiIcon } from '@/ui/icons/UiIcon';
import { Button } from '@/ui/system';
import type { LogLine } from './matchController';
import styles from './Battlefield.module.css';

export interface GameLogProps {
  state: GameState;
  lines: LogLine[];
  onZoom: (instanceId: string) => void;
  onClose: () => void;
  footer?: ReactNode;
}

export function GameLog({ state, lines, onZoom, onClose, footer }: GameLogProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  /*
   * Follow the tail until the player scrolls away, then leave them where they
   * are. This is a ref rather than derived per update on purpose: measuring
   * "am I near the bottom" after appending is measuring the wrong moment, and
   * the log silently stopped following once it grew past one screen.
   */
  const following = useRef(true);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !following.current) return;
    el.scrollTop = el.scrollHeight;
  }, [lines]);

  const onScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    following.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };

  let lastTurn = -1;

  return (
    <aside className={styles.logDock} aria-label="Game log">
      <header className={styles.logHeader}>
        <span className={styles.logTitle}>Game log</span>
        <button type="button" onClick={onClose} aria-label="Hide the log" className={styles.logRef}>
          <UiIcon name="close" size={14} />
        </button>
      </header>

      <div className={styles.logBody} ref={bodyRef} onScroll={onScroll} data-testid="game-log">
        {lines.map((line) => {
          const divider = line.turn !== lastTurn && line.turn > 0;
          if (divider) lastTurn = line.turn;
          return (
            <Fragment key={line.id}>
              {divider ? <div className={styles.logTurn}>Turn {line.turn}</div> : null}
              <div className={styles.logLine}>
                <LineText state={state} line={line} onZoom={onZoom} />
              </div>
            </Fragment>
          );
        })}
      </div>

      {footer ? <div className={styles.logFooter}>{footer}</div> : null}
    </aside>
  );
}

/**
 * Renders a log line with the card names it mentions turned into buttons.
 * The names come from the event's own instance ids, so a rename in card data
 * can never desynchronise the link from the text.
 */
function LineText({
  state,
  line,
  onZoom,
}: {
  state: GameState;
  line: LogLine;
  onZoom: (instanceId: string) => void;
}) {
  if (line.refs.length === 0) return <>{line.text}</>;

  // Build a name -> instanceId map for the ids this line mentions, longest
  // name first so "Solo Miner Bot" is matched before "Solo Miner".
  const named = line.refs
    .map((id) => ({ id, name: profile(state, id).name }))
    .filter((entry) => entry.name.length > 0 && line.text.includes(entry.name))
    .sort((a, b) => b.name.length - a.name.length);

  if (named.length === 0) return <>{line.text}</>;

  const pattern = new RegExp(`(${named.map((n) => escapeRegExp(n.name)).join('|')})`, 'g');
  const parts = line.text.split(pattern);

  return (
    <>
      {parts.map((part, i) => {
        const match = named.find((n) => n.name === part);
        if (!match) return <Fragment key={i}>{part}</Fragment>;
        return (
          <button
            key={i}
            type="button"
            className={styles.logRef}
            style={{ color: `var(--${profile(state, match.id).faction})` }}
            onClick={() => onZoom(match.id)}
          >
            {part}
          </button>
        );
      })}
    </>
  );
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The collapsed state of the dock: one button that brings it back. */
export function LogToggle({ onOpen }: { onOpen: () => void }) {
  return (
    <Button variant="ghost" size="small" icon={<UiIcon name="log" size={14} />} onClick={onOpen}>
      Log
    </Button>
  );
}
